/* eslint-disable @typescript-eslint/no-empty-function */
import { FC, useEffect, useMemo, useState, ChangeEvent } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import { t } from 'i18next';
import { Typography } from '@mui/material';
import styles from './EditPriceDrawer.module.css';
import MaskedInput from '@/components/MaskedInput/MaskedInput';
import Button from '@/components/Button/Button';
import { InputStates } from '@/components/Input/Input.types';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import { useSTKContext } from '@/context/context';
import { isPriceInvalid } from '@/services/menu/menus.helper.service';
import { getStores } from '@/services/modifiers/modifiers.helper.service';
import { useAffectedModifiers, useUpdateProductPrice } from '@/services/modifiers/modifiers.service';
import { VariantModifierPriceUpdateRequest } from '@/types/menus.types';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { EditDrawerProcessing } from '@/pages/Franchisee/SharedComponents/EditDrawerProcessing';
import NamedPriceTable from '@/pages/Franchisee/Menus/EditPriceDrawer/EditPriceTabItems/NamedPriceTable';
import { NamedPriceChangeConfirmation } from '@/pages/Franchisee/Menus/EditPriceDrawer/NamedPriceChangeConfirmation';
import { EditNamedPriceDrawerProps } from '@/pages/Franchisee/Menus/EditPriceDrawer/EditNamedPriceDrawerProps.types';

const LOWEST_VALID_PRICE = '0.00';
const HIGHEST_VALID_PRICE = '5.00';

/** Keep digits and a single dot. Strips every "$" and other non-numeric characters, not only the first. */
function sanitizePriceInput(value: string): string {
  const raw = String(value ?? '').trim();
  if (raw === '') return '';
  const onlyDigitsAndDot = raw.replace(/[^\d.]/g, '');
  if (onlyDigitsAndDot === '') return '';

  const [intPartRaw, ...rest] = onlyDigitsAndDot.split('.');
  const intPart = intPartRaw ?? '';
  const decPart = rest.join('').slice(0, 2);

  return decPart.length ? `${intPart}.${decPart}` : intPart;
}

function buildNamedPriceUpdateRequests(
  namedPriceIds: string[],
  price: string,
  stores: string[],
  modifierCategory: string,
): VariantModifierPriceUpdateRequest[] {
  return namedPriceIds.map((namedPriceId) => ({
    stores,
    price,
    modifierKeys: { namedPriceId },
    modifierCategory,
  }));
}

export const EditNamedPriceDrawer: FC<EditNamedPriceDrawerProps> = ({
  isOpen,
  onClose,
  namedPriceRows,
  onOpenPriceDeviations,
  categoryName,
  afterSave,
}) => {
  const { storesAndGroups } = useSTKContext();
  const stores = getStores(storesAndGroups);
  const [state, setState] = useState(isOpen);
  const [inputPrice, setInputPrice] = useState('');
  const [inputPriceState, setInputPriceState] = useState(InputStates.Default);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [priceIsSaved, setPriceIsSaved] = useState(false);
  const [errorResponse, setErrorResponse] = useState<any | undefined>(undefined);

  const namedPriceIds = useMemo(() => namedPriceRows.map((row) => row.namedPriceId), [namedPriceRows]);
  const isZeroPricedSelection = useMemo(
    () => namedPriceRows.length > 0 && namedPriceRows.every((row) => row.isZeroPriced === true),
    [namedPriceRows],
  );
  const highestValidPrice = isZeroPricedSelection ? '0.00' : HIGHEST_VALID_PRICE;

  const { data: affectedModifiers = [] } = useAffectedModifiers(isOpen, namedPriceIds);
  const {
    mutateAsync: updateProductPrice,
    data: updatePriceResponse,
    isLoading: isSavingPrice = false,
  } = useUpdateProductPrice();

  useEffect(() => {
    if (!isOpen) {
      setInputPrice('');
      setInputPriceState(InputStates.Default);
      setIsConfirmationOpen(false);
      setPriceIsSaved(false);
      setErrorResponse(undefined);
    } else if (isZeroPricedSelection) {
      setInputPrice('0.00');
      setInputPriceState(InputStates.Default);
    }
    setState(isOpen);
  }, [isOpen, isZeroPricedSelection]);

  const isValidPrice = (price: string) => price !== '' && !isPriceInvalid(price, LOWEST_VALID_PRICE, highestValidPrice);

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (isZeroPricedSelection) {
      return;
    }
    const { value } = e.target;
    setInputPrice(value);
    setInputPriceState(
      value !== '' && isPriceInvalid(value, LOWEST_VALID_PRICE, highestValidPrice)
        ? InputStates.Error
        : InputStates.Default,
    );
  };

  const handleContinue = () => {
    setIsConfirmationOpen(true);
  };

  const submittedPrice = isZeroPricedSelection ? '0.00' : sanitizePriceInput(inputPrice);

  const processNamedPrice = () => {
    const requests = buildNamedPriceUpdateRequests(namedPriceIds, submittedPrice, stores, categoryName);

    updateProductPrice(requests, {
      onSuccess: () => {
        setPriceIsSaved(true);
        afterSave();
      },
      onError: (error: any) => {
        if (
          error?.response?.status === 504 ||
          error?.code === 'ECONNABORTED' ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('Gateway Timeout')
        ) {
          setErrorResponse({
            failedStores: [],
            status: 'TIMEOUT',
            message: t('requestProcessingModal.timeout.message'),
          });
          setPriceIsSaved(true);
          afterSave();
          return;
        }
        setErrorResponse({
          failedStores: [],
          status: 'ERROR',
          message: error?.message || t('editMenuPrice.errorMessage'),
        });
        setPriceIsSaved(true);
        afterSave();
      },
    });
  };

  const onCloseDrawer = () => {
    setInputPrice('');
    setInputPriceState(InputStates.Default);
    setIsConfirmationOpen(false);
    setPriceIsSaved(false);
    setErrorResponse(undefined);
    setState(false);
    onClose();
  };

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('editMenuPrice.activateEditMenuPrice')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          <div className={styles.alertBanner}>
            <CalendarAlert requestedVerbiage={false} addCalendarLink={true} warningIcon={true} />
          </div>
          <div className={styles.content}>
            {isSavingPrice ? (
              <EditDrawerProcessing count={stores.length} entityToUpdate={EntityToUpdate.MENU} />
            ) : priceIsSaved ? (
              <EditDrawerSuccessAndFailure
                apiResponse={errorResponse || updatePriceResponse}
                entityToUpdate={EntityToUpdate.MENU}
              />
            ) : (
              <>
                <div className={styles.productSettings}>
                  <strong>{t('editMenuPrice.editMenuPrice')}</strong>
                  {isZeroPricedSelection ? (
                    <p className={styles.zeroPricedNotice}>{t('editMenuPrice.zeroPricedModifierInfo')}</p>
                  ) : (
                    <p className={styles.subtext1}>{t('editMenuPrice.activateInactiveModifiers')}</p>
                  )}
                  <MaskedInput
                    className={styles.input}
                    id="new-price"
                    label={t('editMenuPrice.newPricePoint')}
                    prefix="$"
                    fixedDecimalScale
                    decimalScale={2}
                    value={inputPrice}
                    onChange={handlePriceChange}
                    state={inputPriceState}
                    disabled={isZeroPricedSelection}
                  />
                  <Typography
                    mt={'2px'}
                    mb={'10px'}
                    sx={{
                      fontWeight: '400',
                      fontSize: '12px',
                      color: `${inputPriceState === InputStates.Error ? 'var(--red-700)' : 'var(--black-700)'}`,
                    }}
                  >
                    {isZeroPricedSelection
                      ? `${t('editMenuPrice.zeroPricedRequiredPrice')}: $0.00`
                      : `${t('editMenuPrice.priceRange')}: $${LOWEST_VALID_PRICE} - $${highestValidPrice}`}
                  </Typography>
                  <div className={styles.buttonContainer}>
                    <Button
                      className={styles.submitButton}
                      data-testid="named-price-continue"
                      disabled={namedPriceRows.length === 0 || !isValidPrice(inputPrice)}
                      onClick={handleContinue}
                      type="button"
                    >
                      {t('editMenuPrice.continue')}
                    </Button>
                  </div>
                </div>
                <div className={styles.tableContainer}>
                  <div className={styles.namedPriceTableWrapper}>
                    <NamedPriceTable namedPriceRows={namedPriceRows} onOpenPriceDeviations={onOpenPriceDeviations} />
                  </div>
                  {affectedModifiers.length > 0 && (
                    <div className={styles.affectedModifiers}>
                      <strong>{t('editMenuPrice.affectedModifiers')}</strong>
                      <ul>
                        {affectedModifiers.map((modifier, index) => (
                          <li key={`${modifier}-${index}`} data-testid="affected-modifier-item">
                            {modifier}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
      <NamedPriceChangeConfirmation
        isOpen={isConfirmationOpen}
        onClose={onCloseDrawer}
        onCancel={() => setIsConfirmationOpen(false)}
        onSubmit={() => {
          setIsConfirmationOpen(false);
          processNamedPrice();
        }}
        newPrice={submittedPrice}
        namedPriceRows={namedPriceRows}
      />
    </div>
  );
};

export default EditNamedPriceDrawer;
